/**
 * src/utils/sse/index.js —— SSE 流式连接器（FE-04 共享基石）
 *
 * 用原生 fetch + ReadableStream 消费 SSE 流（不依赖第三方库），
 * 支持重连（GET 订阅带 Last-Event-ID 续传）与按 eventId 去重。
 * 与 UI 完全解耦：不 import 任何 .vue / store；状态通知走回调（FE-03 对接）。
 */

import { BASE_API } from '@/config/api';
import { getToken } from '@/utils/request';
import { parseSseChunk, parseEvent } from './parser';

export { parseSseChunk, parseEvent };

const DEFAULT_RECONNECT = { maxRetries: 3, baseDelayMs: 1000, maxDelayMs: 10000 };

/**
 * 用原生 fetch + ReadableStream 消费 SSE 流。
 * @param {object} options
 * @param {string} options.url 完整 URL
 * @param {'GET'|'POST'} [options.method='GET']
 * @param {object} [options.body] POST 时发送的 JSON body
 * @param {string} [options.token] 认证 token（缺省取 getToken()）
 * @param {Function} [options.onEvent] 收到规范化事件回调 (event)
 * @param {Function} [options.onError] 不可恢复错误回调 (error)
 * @param {Function} [options.onComplete] 流正常结束回调 ()
 * @param {Function} [options.onReconnect] 开始重连回调 (retryCount) —— 重连尝试排定时触发
 * @param {Function} [options.onReconnectSuccess] 重连成功回调 () —— 重连流重新建立时触发
 * @param {object|false} [options.reconnect] 重连配置 {maxRetries, baseDelayMs, maxDelayMs}；false 关闭。
 *        GET 订阅默认开启（重连带 Last-Event-ID 续传）；POST 默认关闭（避免重复创建流）。
 * @param {boolean} [options.dedupe=true] 按 eventId 去重，重复事件丢弃
 * @param {AbortSignal} [options.signal] 外部中止信号
 * @param {Function} [options.fetchImpl] 测试注入用，缺省全局 fetch
 * @returns {{close: Function}} close() 中止连接并停止回调
 */
export function connectTaskStream(options = {}) {
  const {
    url,
    method = 'GET',
    body,
    token = getToken(),
    onEvent,
    onError,
    onComplete,
    onReconnect,
    onReconnectSuccess,
    reconnect = method === 'GET' ? DEFAULT_RECONNECT : false,
    dedupe = true,
    signal,
    fetchImpl = typeof fetch === 'function' ? fetch : null,
  } = options;

  if (!url) {
    throw new Error('connectTaskStream: url 必填');
  }
  if (!fetchImpl) {
    throw new Error('connectTaskStream: 当前环境不支持 fetch');
  }

  let closed = false;
  let retryCount = 0;
  let reconnecting = false;
  let lastEventId;
  let controller;
  let retryTimer;
  const seen = new Set();

  const emitFrame = (frame) => {
    if (closed) {
      return;
    }
    if (frame.data === '[DONE]') {
      finish();
      return;
    }
    let json;
    try {
      json = JSON.parse(frame.data);
    } catch (error) {
      return; // 非 JSON 帧（异常帧）忽略，流继续
    }
    const event = parseEvent(json);
    if (!event) {
      return;
    }
    if (dedupe && event.eventId) {
      if (seen.has(event.eventId)) {
        return;
      }
      seen.add(event.eventId);
    }
    if (event.eventId) {
      lastEventId = event.eventId;
    }
    if (typeof onEvent === 'function') {
      try {
        onEvent(event);
      } catch (error) {
        closed = true; // 消费方异常：不重连、不回调，向上抛出
        throw error;
      }
    }
  };

  const finish = () => {
    if (closed) {
      return;
    }
    closed = true;
    if (typeof onComplete === 'function') {
      onComplete();
    }
  };

  const fail = (error) => {
    if (closed) {
      return;
    }
    const retryable =
      !(error instanceof HttpError && error.status >= 400 && error.status < 500 && error.status !== 429);
    if (retryable && reconnect && retryCount < reconnect.maxRetries) {
      retryCount += 1;
      reconnecting = true;
      if (typeof onReconnect === 'function') {
        onReconnect(retryCount);
      }
      retryTimer = setTimeout(() => attempt(), backoffDelay(reconnect, retryCount));
      return;
    }
    closed = true;
    if (typeof onError === 'function') {
      onError(error);
    }
  };

  const attempt = async () => {
    if (closed) {
      return;
    }
    controller = new AbortController();
    const headers = {};
    if (token) {
      headers.token = token;
    }
    if (method === 'POST' && body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (method === 'GET' && lastEventId) {
      headers['Last-Event-ID'] = lastEventId;
    }
    const abortSignal = signal ? anyAbort([signal, controller.signal]) : controller.signal;

    try {
      const response = await fetchImpl(url, {
        method,
        headers,
        body: method === 'POST' && body !== undefined ? JSON.stringify(body) : undefined,
        signal: abortSignal,
      });
      if (!response.ok) {
        throw new HttpError(response.status);
      }
      if (reconnecting) {
        reconnecting = false;
        if (typeof onReconnectSuccess === 'function') {
          onReconnectSuccess();
        }
      }
      if (!response.body || typeof response.body.getReader !== 'function') {
        throw new Error('connectTaskStream: 当前环境不支持 ReadableStream');
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const { complete, rest } = splitFrames(buffer);
        buffer = rest;
        if (complete) {
          for (const frame of parseSseChunk(complete)) {
            emitFrame(frame);
            if (closed) {
              break;
            }
          }
        }
        if (closed) {
          break;
        }
      }
      if (!closed) {
        finish();
      }
    } catch (error) {
      if (closed) {
        return; // 主动关闭或消费方异常
      }
      if (error.name === 'AbortError') {
        closed = true;
        return;
      }
      fail(error);
    }
  };

  attempt();

  return {
    close() {
      closed = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      if (controller) {
        controller.abort();
      }
    },
  };
}

/**
 * POST /api/ai/task/stream：一次性创建并流式执行。
 * body 对齐后端 AiTaskRequest：{ query, conversationId }。
 * 默认不自动重连（重连会重复创建流）。
 */
export function streamNewTask({ query, conversationId = '', token, ...rest }) {
  return connectTaskStream({
    url: `${BASE_API}/api/ai/task/stream`,
    method: 'POST',
    body: { query, conversationId },
    token,
    reconnect: false,
    ...rest,
  });
}

/**
 * GET /api/ai/tasks/{taskId}/events：订阅任务事件流。
 * 重连时自动携带 Last-Event-ID 请求头续传。
 */
export function subscribeTaskEvents({ taskId, token, ...rest }) {
  return connectTaskStream({
    url: `${BASE_API}/api/ai/tasks/${taskId}/events`,
    method: 'GET',
    token,
    ...rest,
  });
}

/* ---------------- 内部工具 ---------------- */

/** 从累积文本中切出完整帧（以最后一个空行为界），保留尾部不完整帧 */
function splitFrames(text) {
  const normalized = text.replace(/\r\n/g, '\n');
  const lastBreak = normalized.lastIndexOf('\n\n');
  if (lastBreak === -1) {
    return { complete: '', rest: normalized };
  }
  return {
    complete: normalized.slice(0, lastBreak),
    rest: normalized.slice(lastBreak + 2),
  };
}

/** 指数退避（无抖动，保证可测） */
function backoffDelay(config, attempt) {
  const base = config.baseDelayMs || 0;
  const max = config.maxDelayMs || 0;
  return Math.min(max || base, base * 2 ** (attempt - 1));
}

/** 合并多个 AbortSignal：任一中止即中止 */
function anyAbort(signals) {
  const controller = new AbortController();
  for (const s of signals) {
    if (!s) {
      continue;
    }
    if (s.aborted) {
      controller.abort();
      break;
    }
    s.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return controller.signal;
}

class HttpError extends Error {
  constructor(status) {
    super(`SSE 请求失败: HTTP ${status}`);
    this.name = 'HttpError';
    this.status = status;
  }
}